import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Send } from "lucide-react";

export default function MessagesPage() {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = () => api.get("/messages").then((r) => setMessages(r.data.messages || [])).catch(() => {});

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post("/messages", { body });
      setBody("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-messages" className="space-y-6 h-full">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-3">— Secure Messaging</div>
        <h1 className="font-serif text-5xl text-[#0F292F] font-light tracking-tight">
          Conversations with Harold Hodges
        </h1>
      </div>

      <div className="bg-white border border-black/10 flex flex-col h-[68vh]">
        <div className="px-6 py-4 border-b border-black/10 flex items-center gap-3">
          <div className="w-10 h-10 bg-[#8E6A2B] flex items-center justify-center font-serif text-[#0F292F] text-xl">H</div>
          <div>
            <div className="font-medium text-[#0F292F]">Harold Hodges, EA</div>
            <div className="text-xs text-[#52525B]">Founder · Replies within one business day</div>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-4 bg-[#F9F8F6]/40">
          {messages.length === 0 && (
            <div className="text-center text-[#52525B] py-12">No messages yet. Send the first one below.</div>
          )}
          {messages.map((m) => {
            const isClient = m.sender === "client";
            return (
              <div key={m.id} data-testid={`msg-${m.id}`} className={`flex ${isClient ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[70%] p-4 ${isClient ? "bg-[#0F292F] text-white" : "bg-white border border-black/10 text-[#1A1A1A]"}`}>
                  <div className={`text-[10px] uppercase tracking-[0.2em] mb-2 ${isClient ? "text-[#CFA968]" : "text-[#8E6A2B]"}`}>
                    {m.sender_name} · {new Date(m.created_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                  </div>
                  <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.body}</div>
                </div>
              </div>
            );
          })}
        </div>

        <form onSubmit={send} data-testid="msg-form" className="border-t border-black/10 p-4 flex gap-3 bg-white">
          <input
            data-testid="msg-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type a secure message..."
            className="flex-1 he-input"
          />
          <button type="submit" data-testid="msg-send" disabled={sending || !body.trim()} className="btn-primary disabled:opacity-60">
            <Send size={16} /> Send
          </button>
        </form>
      </div>

      <p className="text-xs text-[#52525B]">
        Messages are stored encrypted at rest. Do not share full Social Security numbers or passwords through this channel.
      </p>
    </div>
  );
}
